import type { StatusApplication, StatusId } from '@jianghu/game-data';

export interface ActiveStatus {
  id: StatusId;
  magnitude: number;
  /** Absolute expiry time (ms, same clock as `now`). */
  until: number;
}

export interface StatusAggregate {
  speedMul: number;
  stunned: boolean;
}

export const STATUS_INFO: Readonly<Record<StatusId, { name: string; debuff: boolean; bit: number }>> = {
  slow: { name: 'Slowed', debuff: true, bit: 1 },
  stun: { name: 'Stunned', debuff: true, bit: 2 },
};

/**
 * Status effects on one combatant. Re-applying a status refreshes its duration and keeps the
 * strongest magnitude (no stacking), which keeps crowd control predictable.
 */
export class StatusSet {
  private readonly list: ActiveStatus[] = [];

  apply(app: StatusApplication, now: number): ActiveStatus {
    const until = now + app.durationMs;
    const existing = this.list.find((s) => s.id === app.status);
    if (existing) {
      existing.until = Math.max(existing.until, until);
      existing.magnitude = Math.max(existing.magnitude, app.magnitude);
      return existing;
    }
    const s: ActiveStatus = { id: app.status, magnitude: app.magnitude, until };
    this.list.push(s);
    return s;
  }

  /** Removes expired statuses; returns the ids removed. */
  prune(now: number): StatusId[] {
    const removed: StatusId[] = [];
    for (let i = this.list.length - 1; i >= 0; i--) {
      const s = this.list[i] as ActiveStatus;
      if (s.until <= now) {
        removed.push(s.id);
        this.list.splice(i, 1);
      }
    }
    return removed;
  }

  clear(): void {
    this.list.length = 0;
  }

  has(id: StatusId): boolean {
    return this.list.some((s) => s.id === id);
  }

  aggregate(): StatusAggregate {
    let slow = 0;
    let stunned = false;
    for (const s of this.list) {
      if (s.id === 'slow') slow = Math.max(slow, s.magnitude);
      else if (s.id === 'stun') stunned = true;
    }
    return { speedMul: 1 - Math.min(0.9, slow), stunned };
  }

  bits(): number {
    let b = 0;
    for (const s of this.list) b |= STATUS_INFO[s.id].bit;
    return b;
  }

  snapshot(now: number): { id: StatusId; remainingMs: number }[] {
    return this.list.map((s) => ({ id: s.id, remainingMs: Math.max(0, s.until - now) }));
  }
}
